import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronDown, Loader2, Puzzle, RefreshCw, Search, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@reflex/ui/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import {
  InlineDocsHint,
  PageDescription,
  PanelTitle,
  SectionLabel,
} from '@reflex/ui/components/layout/Typography';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { PluginIcon } from '@reflex/ui/components/shared/PluginIcon';
import { CanGuard } from '@reflex/ui/client/permissions';
import { TOP_PROGRESS } from '@/components/layout/top-progress-store';
import {
  MissingCapabilityBadge,
  RequiredCapabilityBadge,
} from '@/components/plugins/CapabilityBadges';
import { PluginInstallationStatusPill } from '@/components/plugins/PluginInstallationStatusPill';
import { ReleaseStatusBadge } from '@/components/plugins/ReleaseStatusBadge';
import { Badge } from '@reflex/ui/components/ui/badge';
import { ShieldOff } from 'lucide-react';
import type { PluginReleaseChannel } from '@/lib/types';
import { useAuth } from '@/hooks/auth/useAuth';
import { invalidateOrgPluginInstallationQueries } from '@/hooks/plugins/invalidateOrgPluginInstallationQueries';
import { orgPluginsQueryKey } from '@/hooks/plugins/useOrgPlugins';
import { api } from '@/lib/api';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { cn } from '@reflex/ui/lib/utils';
import type { InstallationView } from '@/lib/types';

/** Third-party Connect integrations, listed above in-app plugins. */
const INTEGRATION_PLUGINS = new Set([
  'slack',
  'github',
  'linear',
  'jira',
  'sentry',
  'pylon',
  'pagerduty',
  'incident-io',
  'hubspot',
  'google-workspace',
  'ashby',
  'canny',
]);

/** Integrations the catalog should list first (Slack, GitHub, Linear, Jira). */
const PRIORITIZED_INTEGRATIONS = ['slack', 'github', 'linear', 'jira'] as const;

function prioritizeIntegrations(rows: InstallationView[]): InstallationView[] {
  return [...rows].sort((a, b) => {
    const ai = PRIORITIZED_INTEGRATIONS.indexOf(
      a.pluginName as (typeof PRIORITIZED_INTEGRATIONS)[number],
    );
    const bi = PRIORITIZED_INTEGRATIONS.indexOf(
      b.pluginName as (typeof PRIORITIZED_INTEGRATIONS)[number],
    );
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

/**
 * Per-organization plugin install/uninstall picker. Renders installed
 * integrations (if any) above other plugins, then available integrations
 * above other available plugins, then required system plugins.
 *
 * Used by:
 *   - `OrgPluginInstallPage` (the full `/orgs/${slug}/org/plugins` route)
 *   - `OrgPluginsStage` inside the per-org setup wizard
 *
 * The component is presentation-only: data and search filtering are owned
 * by the caller so each surface can pair it with its own header, search
 * input, layout chrome, etc.
 */
export interface OrgPluginPickerData {
  installed: InstallationView[];
  available: InstallationView[];
  system: InstallationView[];
}

/**
 * Navigation `state` forwarded onto a plugin row's Settings link so the
 * destination page (an integration settings page that mounts
 * `IntegrationPageLayout`) can show a contextual "Back to …" affordance.
 * The integrations list passes `{ fromHref: <integrations list>, fromPlugin:
 * 'All integrations' }` so a provider page reached from that list keeps its
 * back button; callers that don't set it omit the prop so direct/Configure
 * navigations stay back-button-free.
 */
export interface PluginSettingsLinkState {
  fromHref: string;
  fromPlugin?: string;
}

export interface OrgPluginPickerProps {
  /** Pre-filtered installation rows split by section. */
  data: OrgPluginPickerData | undefined;
  /** Active organization id — required for install/uninstall mutations. */
  orgId: string | null;
  /**
   * Whether the caller is currently filtering the rows (e.g. via a
   * search input). When true, empty sections are hidden entirely
   * instead of rendering their empty-state copy — surfacing every
   * empty group is noisier than helpful while searching.
   */
  searchActive?: boolean;
  /**
   * Optional copy override for the "no plugins anywhere" empty state.
   * The default copy is generic (`"There are no installable or
   * required plugins on this server."`). The wizard surface passes a
   * more contextual message so the user understands the org has no
   * plugins to manage.
   */
  emptyTitle?: string;
  /** Companion description for the empty state. */
  emptyDescription?: string;
  /**
   * Optional callback fired when the user clicks a plugin's name.
   * When provided, the plugin name renders as a `<button>` that calls
   * this handler instead of a `<Link>` to the detail page — useful
   * in contexts where navigation away is undesirable or blocked
   * (e.g. the org-setup wizard where the detail route is gated).
   * When omitted, today's `<Link>` navigation is preserved.
   */
  onPluginSelect?: (pluginName: string) => void;
  /**
   * When set, installed plugins whose name appears in this map get a
   * Settings button targeting their integration settings page. Prefer
   * {@link settingsHrefForPlugin} when the host needs a custom target
   * (integration detail vs plugin detail).
   */
  integrationPathByPlugin?: ReadonlyMap<string, string>;
  /**
   * Resolve the Settings button href for a plugin row. When omitted
   * (or when it returns null), the row has no Settings button — the
   * name already links to plugin detail.
   */
  settingsHrefForPlugin?: (pluginName: string) => string | null;
  /**
   * Optional `location.state` to attach to each row's Settings link. Used by
   * the integrations list so the destination integration page can render a
   * "Back to All integrations" affordance. Omitted by surfaces whose
   * Settings link targets plugin detail (which carries its own back chrome).
   */
  settingsLinkState?: PluginSettingsLinkState;
}

export function OrgPluginPicker({
  data,
  orgId,
  searchActive = false,
  emptyTitle,
  emptyDescription,
  onPluginSelect,
  integrationPathByPlugin,
  settingsHrefForPlugin,
  settingsLinkState,
}: OrgPluginPickerProps) {
  const split = useMemo(() => {
    if (!data) return null;
    const isIntegration = (row: InstallationView) => INTEGRATION_PLUGINS.has(row.pluginName);
    return {
      installedIntegrations: prioritizeIntegrations(data.installed.filter(isIntegration)),
      installedPlugins: data.installed.filter((row) => !isIntegration(row)),
      availableIntegrations: prioritizeIntegrations(data.available.filter(isIntegration)),
      availablePlugins: data.available.filter((row) => !isIntegration(row)),
      system: data.system,
    };
  }, [data]);

  if (!data || !split) return null;
  const matchCount = data.installed.length + data.available.length + data.system.length;

  if (matchCount === 0 && searchActive) {
    return (
      <EmptyState
        icon={<Search className="h-5 w-5 text-muted-foreground" />}
        title="No matching plugins"
        description="Try a different search term, or clear the search to see every plugin."
      />
    );
  }
  if (matchCount === 0) {
    return (
      <EmptyState
        icon={<Puzzle className="h-5 w-5 text-muted-foreground" />}
        title={emptyTitle ?? 'No plugins to manage'}
        description={
          emptyDescription ?? 'There are no installable or required plugins on this server.'
        }
      />
    );
  }
  return (
    <div className="flex flex-col gap-8">
      <PluginSection
        label="Installed integrations"
        testId="plugins-section-installed-integrations"
        rows={split.installedIntegrations}
        orgId={orgId}
        emptyMessage="No installed integrations match your search."
        searchActive={searchActive}
        hideWhenEmpty
        onPluginSelect={onPluginSelect}
        integrationPathByPlugin={integrationPathByPlugin}
        settingsHrefForPlugin={settingsHrefForPlugin}
        settingsLinkState={settingsLinkState}
      />
      <PluginSection
        label="Installed plugins"
        testId="plugins-section-installed"
        rows={split.installedPlugins}
        orgId={orgId}
        emptyMessage={
          searchActive
            ? 'No installed plugins match your search.'
            : 'No plugins are installed for this organization yet.'
        }
        searchActive={searchActive}
        hideWhenEmpty={split.installedIntegrations.length > 0}
        onPluginSelect={onPluginSelect}
        integrationPathByPlugin={integrationPathByPlugin}
        settingsHrefForPlugin={settingsHrefForPlugin}
        settingsLinkState={settingsLinkState}
        docsSlug="administration/organization-onboarding#installed-plugins"
      />
      <PluginSection
        label="Available integrations"
        testId="plugins-section-available-integrations"
        rows={split.availableIntegrations}
        orgId={orgId}
        emptyMessage="No available integrations match your search."
        searchActive={searchActive}
        hideWhenEmpty
        onPluginSelect={onPluginSelect}
        integrationPathByPlugin={integrationPathByPlugin}
        settingsHrefForPlugin={settingsHrefForPlugin}
        settingsLinkState={settingsLinkState}
      />
      <PluginSection
        label="Available plugins"
        testId="plugins-section-available"
        rows={split.availablePlugins}
        orgId={orgId}
        emptyMessage={
          searchActive
            ? 'No available plugins match your search.'
            : 'Every available plugin is installed.'
        }
        searchActive={searchActive}
        hideWhenEmpty={split.availableIntegrations.length > 0}
        onPluginSelect={onPluginSelect}
        integrationPathByPlugin={integrationPathByPlugin}
        settingsHrefForPlugin={settingsHrefForPlugin}
        settingsLinkState={settingsLinkState}
        docsSlug="administration/organization-onboarding#available-plugins"
      />
      <PluginSection
        label="Required system plugins"
        testId="plugins-section-system"
        rows={split.system}
        orgId={orgId}
        emptyMessage={searchActive ? 'No system plugins match your search.' : 'No system plugins.'}
        system
        searchActive={searchActive}
        onPluginSelect={onPluginSelect}
        integrationPathByPlugin={integrationPathByPlugin}
        settingsHrefForPlugin={settingsHrefForPlugin}
        settingsLinkState={settingsLinkState}
        collapsible
        docsSlug="administration/organization-onboarding#required-system-plugins"
        description="Bundled plugins the host relies on (auth, MCP host, docs, …). Listed for transparency. They can't be uninstalled."
      />
    </div>
  );
}

interface PluginSectionProps {
  label: string;
  testId: string;
  rows: InstallationView[];
  orgId: string | null;
  emptyMessage: string;
  system?: boolean;
  searchActive: boolean;
  /** Hide the section entirely when it has no rows, even when not searching. */
  hideWhenEmpty?: boolean;
  onPluginSelect?: (pluginName: string) => void;
  integrationPathByPlugin?: ReadonlyMap<string, string>;
  settingsHrefForPlugin?: (pluginName: string) => string | null;
  /** See {@link OrgPluginPickerProps.settingsLinkState}. */
  settingsLinkState?: PluginSettingsLinkState;
  /**
   * When true, the section's body is wrapped in a Collapsible and the
   * header becomes a clickable trigger. Default collapsed; the section
   * auto-expands whenever `searchActive` is true so search matches are
   * always visible without an extra click.
   */
  collapsible?: boolean;
  /**
   * Optional docs slug (with optional `#anchor`) rendered as a small
   * `DocsHint` icon next to the section label. Lets the picker surface
   * inline guidance for each group — what "Installed" means, what lives
   * under "Available", and why the system plugins are listed but
   * un-uninstallable. Renders nothing when no docs renderer is registered.
   */
  docsSlug?: string;
  /**
   * Optional one-line description rendered below the header. Stays
   * visible even when the section is collapsed so users can read what a
   * group contains before deciding to expand it — especially important
   * for the system-plugin section, which is otherwise opaque.
   */
  description?: string;
}

function PluginSection({
  label,
  testId,
  rows,
  orgId,
  emptyMessage,
  system,
  searchActive,
  hideWhenEmpty,
  onPluginSelect,
  integrationPathByPlugin,
  settingsHrefForPlugin,
  settingsLinkState,
  collapsible,
  docsSlug,
  description,
}: PluginSectionProps) {
  if ((searchActive || hideWhenEmpty) && rows.length === 0) return null;

  const body =
    rows.length === 0 ? (
      <p className="text-xs text-muted-foreground/80">{emptyMessage}</p>
    ) : (
      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li key={row.pluginName}>
            <PluginCard
              row={row}
              orgId={orgId}
              system={system}
              onPluginSelect={onPluginSelect}
              integrationPathByPlugin={integrationPathByPlugin}
              settingsHrefForPlugin={settingsHrefForPlugin}
              settingsLinkState={settingsLinkState}
            />
          </li>
        ))}
      </ul>
    );

  if (collapsible) {
    return (
      <CollapsibleSection
        label={label}
        testId={testId}
        count={rows.length}
        searchActive={searchActive}
        docsSlug={docsSlug}
        description={description}
      >
        {body}
      </CollapsibleSection>
    );
  }

  return (
    <section data-testid={testId} className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <SectionLabel>{label}</SectionLabel>
        {rows.length > 0 && (
          <span className="text-2xs font-medium tracking-wider text-muted-foreground/50">
            · {rows.length}
          </span>
        )}
        {docsSlug && <InlineDocsHint slug={docsSlug} className="self-center" />}
      </div>
      {description && (
        <p data-testid={`${testId}-description`} className="text-xs text-muted-foreground/80">
          {description}
        </p>
      )}
      {body}
    </section>
  );
}

interface CollapsibleSectionProps {
  label: string;
  testId: string;
  count: number;
  searchActive: boolean;
  children: React.ReactNode;
  /** See `PluginSectionProps.docsSlug`. */
  docsSlug?: string;
  /** See `PluginSectionProps.description`. */
  description?: string;
}

/**
 * Collapsible variant of `PluginSection`'s outer chrome. Defaults to
 * collapsed so heavy/static groups (e.g. "Required system plugins") stay
 * out of the way until the user opts in. When the parent is actively
 * filtering (`searchActive`), the section force-expands so any matches
 * are visible without an extra click — the user's manual toggle is
 * remembered and reapplied as soon as the search clears.
 */
function CollapsibleSection({
  label,
  testId,
  count,
  searchActive,
  children,
  docsSlug,
  description,
}: CollapsibleSectionProps) {
  const [userOpen, setUserOpen] = useState(false);
  const open = searchActive || userOpen;
  return (
    <Collapsible
      open={open}
      onOpenChange={(next) => {
        // Ignore programmatic open-while-searching toggles — the user's
        // intent is captured only when they actively click the trigger.
        if (!searchActive) setUserOpen(next);
      }}
      data-testid={testId}
      className="flex flex-col gap-2.5"
    >
      <div className="flex items-center gap-2">
        {/* Trigger sizes to its content (no `flex-1`) so the inline
            docs hint sits next to the label/count instead of being
            pushed to the far right of the row. */}
        <CollapsibleTrigger
          data-testid={`${testId}-trigger`}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
          className="group flex items-baseline gap-2 rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <ChevronDown
            aria-hidden="true"
            className="h-3 w-3 shrink-0 self-center text-muted-foreground/60 transition-transform duration-200 group-data-[state=closed]:-rotate-90"
          />
          <SectionLabel>{label}</SectionLabel>
          {count > 0 && (
            <span className="text-2xs font-medium tracking-wider text-muted-foreground/50">
              · {count}
            </span>
          )}
        </CollapsibleTrigger>
        {/* Rendered as a sibling of the trigger — putting it inside the
            `CollapsibleTrigger` would nest a button inside a button and
            also toggle the section on every docs-icon click. */}
        {docsSlug && <InlineDocsHint slug={docsSlug} className="self-center" />}
      </div>
      {description && (
        <p data-testid={`${testId}-description`} className="text-xs text-muted-foreground/80">
          {description}
        </p>
      )}
      <CollapsibleContent data-testid={`${testId}-content`}>{children}</CollapsibleContent>
    </Collapsible>
  );
}

interface PluginCardProps {
  row: InstallationView;
  orgId: string | null;
  system?: boolean;
  onPluginSelect?: (pluginName: string) => void;
  integrationPathByPlugin?: ReadonlyMap<string, string>;
  settingsHrefForPlugin?: (pluginName: string) => string | null;
  settingsLinkState?: PluginSettingsLinkState;
}

function PluginCard({
  row,
  orgId,
  system,
  onPluginSelect,
  integrationPathByPlugin,
  settingsHrefForPlugin,
  settingsLinkState,
}: PluginCardProps) {
  const orgRoutes = useOrgRoutes();
  const { can, authEnabled, isPlatformAdmin } = useAuth();
  const detailHref = orgRoutes.plugin(row.pluginName);

  const installedNames = useInstalledPluginNames(orgId);
  const missingDeps = useMemo(() => {
    if (system) return [];
    if (row.status === 'installed') return [];
    return row.dependencies.filter((dep) => installedNames && !installedNames.has(dep));
  }, [row.dependencies, row.status, system, installedNames]);

  const isTransient = row.status === 'installing' || row.status === 'uninstalling';
  // RBAC mirrors `CanGuard`: when auth is dormant, treat the user as an
  // admin so the action button shows in self-host setups.
  const canInstall = !authEnabled || isPlatformAdmin || can('plugins:install');
  const showActionButton = !system && Boolean(orgId) && canInstall;
  // Drop the redundant "Installed" pill when the action button already
  // conveys state. Keep it for transient states (so progress is obvious),
  // for system plugins (so "Required" is communicated), and for users who
  // don't have install permissions (so they still see the status).
  const showStatusPill = system || isTransient || !showActionButton;

  // Settings opens integration detail when mapped, otherwise plugin detail.
  const resolvedSettingsHref = (() => {
    if (settingsHrefForPlugin) return settingsHrefForPlugin(row.pluginName);
    const integrationRelative = integrationPathByPlugin?.get(row.pluginName);
    if (integrationRelative?.startsWith('/')) return integrationRelative;
    return null;
  })();

  return (
    <article
      data-testid={`plugin-row-${row.pluginName}`}
      data-status={row.status}
      className={cn(
        'group relative flex flex-col gap-3 rounded-lg border border-border/40 bg-card px-4 py-3.5',
        'transition-colors duration-150',
        'hover:border-border hover:bg-muted/30',
        'focus-within:border-border focus-within:ring-2 focus-within:ring-ring/30',
        'sm:flex-row sm:items-start',
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <PluginIcon icon={row.icon} size="sm" className="shrink-0" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2">
            {onPluginSelect ? (
              <button
                type="button"
                onClick={() => onPluginSelect(row.pluginName)}
                data-testid={`plugin-row-link-${row.pluginName}`}
                aria-label={`View ${row.pluginName} plugin details`}
                className={cn(
                  'min-w-0 flex-1 rounded-sm text-left outline-none',
                  "before:absolute before:inset-0 before:rounded-lg before:content-['']",
                  'focus-visible:before:ring-2 focus-visible:before:ring-ring/40',
                )}
              >
                <PanelTitle
                  as="h3"
                  size="sm"
                  className="text-foreground/90 group-hover:underline group-hover:text-foreground"
                >
                  {row.pluginName}
                </PanelTitle>
              </button>
            ) : (
              <Link
                to={detailHref}
                data-testid={`plugin-row-link-${row.pluginName}`}
                aria-label={`View ${row.pluginName} plugin details`}
                className={cn(
                  'min-w-0 flex-1 rounded-sm outline-none',
                  "before:absolute before:inset-0 before:rounded-lg before:content-['']",
                  'focus-visible:before:ring-2 focus-visible:before:ring-ring/40',
                )}
              >
                <PanelTitle
                  as="h3"
                  size="sm"
                  className="text-foreground/90 group-hover:underline group-hover:text-foreground"
                >
                  {row.pluginName}
                </PanelTitle>
              </Link>
            )}
            {showStatusPill && <PluginInstallationStatusPill status={row.status} system={system} />}
            {row.releaseStatus && row.releaseStatus !== 'released' && (
              <ReleaseStatusBadge status={row.releaseStatus} testIdSuffix={row.pluginName} />
            )}
            {row.unavailableReason === 'missing_capabilities' &&
            (row.missingCapabilities?.length ?? 0) > 0 ? (
              <MissingCapabilityBadge
                missingCapabilities={row.missingCapabilities ?? []}
                testIdSuffix={row.pluginName}
              />
            ) : row.unavailableReason === 'channel_not_enabled' && row.requiredReleaseChannel ? (
              <ChannelNotEnabledBadge
                channel={row.requiredReleaseChannel}
                testIdSuffix={row.pluginName}
              />
            ) : (
              (row.requiredCapabilities?.length ?? 0) > 0 && (
                <RequiredCapabilityBadge
                  requiredCapabilities={row.requiredCapabilities ?? []}
                  testIdSuffix={row.pluginName}
                />
              )
            )}
          </div>
          {row.description && (
            <PageDescription className="mt-0 text-xs">{row.description}</PageDescription>
          )}
          {row.lastError && (
            <Alert variant="destructive" className="relative z-10 mt-2">
              <AlertTitle>Last action failed</AlertTitle>
              <AlertDescription>{row.lastError}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
      <div className="relative z-10 flex w-full shrink-0 items-center gap-2 sm:w-auto sm:self-center">
        {resolvedSettingsHref && (
          <Button asChild variant="outline" size="sm" className="min-w-0 flex-1 sm:flex-none">
            <Link
              to={resolvedSettingsHref}
              state={settingsLinkState}
              data-testid={`plugin-settings-${row.pluginName}`}
              aria-label={`Settings for ${row.pluginName}`}
            >
              <Settings className="h-3.5 w-3.5" />
              Settings
            </Link>
          </Button>
        )}
        {!system && orgId && (
          <CanGuard permission="plugins:install">
            <PluginActionButton
              row={row}
              orgId={orgId}
              missingDeps={missingDeps}
              className="min-w-0 flex-1 sm:flex-none"
            />
          </CanGuard>
        )}
      </div>
    </article>
  );
}

interface PluginActionButtonProps {
  row: InstallationView;
  orgId: string;
  missingDeps: string[];
  className?: string;
}

function PluginActionButton({ row, orgId, missingDeps, className }: PluginActionButtonProps) {
  const queryClient = useQueryClient();
  const isInstalled = row.status === 'installed';
  const isTransient = row.status === 'installing' || row.status === 'uninstalling';

  const mutation = useMutation({
    // Install/uninstall blocks on a base image rebuild and can run for minutes.
    // The plugin's own "Installing…" pill is the affordance — keep it off the
    // global top progress bar so the app doesn't look stuck.
    meta: { [TOP_PROGRESS]: false },
    mutationFn: async () => {
      if (isInstalled) {
        return api.uninstallOrgPlugin(orgId, row.pluginName);
      }
      return api.installOrgPlugin(orgId, row.pluginName);
    },
    onSuccess: () => {
      toast.success(isInstalled ? `Uninstalled ${row.pluginName}` : `Installed ${row.pluginName}`);
      invalidateOrgPluginInstallationQueries(queryClient, orgId, [row.pluginName]);
    },
    onError: (err) => {
      toast.error(isInstalled ? 'Uninstall failed' : 'Install failed', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  if (isTransient) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled
        data-testid={`plugin-action-${row.pluginName}`}
        aria-label={
          row.status === 'installing'
            ? `Installing ${row.pluginName}`
            : `Uninstalling ${row.pluginName}`
        }
        className={cn('gap-1', className)}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        {row.status === 'installing' ? 'Installing…' : 'Uninstalling…'}
      </Button>
    );
  }

  if (row.lastError) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        data-testid={`plugin-action-${row.pluginName}`}
        aria-label={`Retry ${isInstalled ? 'uninstalling' : 'installing'} ${row.pluginName}`}
        className={cn('gap-1', className)}
      >
        <RefreshCw className="h-3 w-3" />
        Retry
      </Button>
    );
  }

  if (!isInstalled && missingDeps.length > 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn('min-w-0', className)}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled
              className="w-full"
              data-testid={`plugin-action-${row.pluginName}`}
              aria-label={`Install ${row.pluginName} (requires ${missingDeps.join(', ')})`}
            >
              Install
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>Requires: {missingDeps.join(', ')}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Button
      type="button"
      variant={isInstalled ? 'outline' : 'default'}
      size="sm"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      data-testid={`plugin-action-${row.pluginName}`}
      aria-label={isInstalled ? `Uninstall ${row.pluginName}` : `Install ${row.pluginName}`}
      className={className}
    >
      {mutation.isPending
        ? isInstalled
          ? 'Uninstalling…'
          : 'Installing…'
        : isInstalled
          ? 'Uninstall'
          : 'Install'}
    </Button>
  );
}

/**
 * Read the cached org plugins list so dependency-status badges and the
 * "Install" disabled-tooltip stay live without firing extra fetches.
 * Returns `null` until the cache is populated.
 */
function useInstalledPluginNames(orgId: string | null): Set<string> | null {
  const queryClient = useQueryClient();
  if (!orgId) return null;
  const cached = queryClient.getQueryData<{
    installed: InstallationView[];
    system: InstallationView[];
  }>(orgPluginsQueryKey(orgId));
  if (!cached) return null;
  return new Set([
    ...cached.installed.map((p) => p.pluginName),
    ...cached.system.map((p) => p.pluginName),
  ]);
}

interface ChannelNotEnabledBadgeProps {
  channel: PluginReleaseChannel;
  testIdSuffix: string;
}

/**
 * Destructive "Channel disabled" badge surfaced for stranded installs
 * whose pre-release channel was turned off after the plugin was
 * installed. The reverse case (a plugin available but not yet installed)
 * never appears because the install service filters those rows out of
 * the catalog entirely.
 */
function ChannelNotEnabledBadge({ channel, testIdSuffix }: ChannelNotEnabledBadgeProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="destructive"
          className="gap-1 text-2xs font-normal"
          data-testid={`plugin-row-${testIdSuffix}-channel-disabled`}
        >
          <ShieldOff className="h-3 w-3" />
          {channel} channel off
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        Re-enable the {channel} channel at the top of the page to keep using this plugin.
      </TooltipContent>
    </Tooltip>
  );
}
